/**
 * Material pick list of the open job: needed, in the van, to order. The van stock is kept on
 * this device (localStorage); "Take from the van" lowers it by what the job uses. The part to
 * order goes to the wholesaler as Excel or a reference;quantity CSV.
 */
import { useEffect, useMemo, useState } from 'react';
import type { Project } from '@elec/domain';
import type { PickLine, VanStock } from '@elec/export';
import type { Locale, Translate } from '@elec/i18n';
import { Dialog } from './ui.tsx';

const KEY = 'tablo.van.v1';

export function readVan(): VanStock {
  try {
    const v = JSON.parse(localStorage.getItem(KEY) ?? '{}') as unknown;
    return v && typeof v === 'object' ? (v as VanStock) : {};
  } catch {
    return {};
  }
}
function writeVan(v: VanStock) {
  try {
    localStorage.setItem(KEY, JSON.stringify(v));
  } catch {
    // Private mode: the van stock lasts for this session only.
  }
}

export function PickListDialog(props: {
  project: Project;
  t: Translate;
  locale: Locale;
  save(bytes: Uint8Array, name: string, ext: string, mime: string): void;
  notify(msg: string): void;
  onClose(): void;
}) {
  const { t, project } = props;
  const [x, setX] = useState<typeof import('@elec/export') | null>(null);
  const [van, setVan] = useState<VanStock>(readVan);
  useEffect(() => {
    void import('@elec/export').then(setX);
  }, []);
  const lines: PickLine[] = useMemo(() => (x ? x.pickList(project, van) : []), [x, project, van]);
  const setStock = (key: string, n: number) => {
    const next = { ...van };
    if (n > 0) next[key] = n;
    else delete next[key];
    setVan(next);
    writeVan(next);
  };
  const name = project.meta.title || 'Tablo';
  const toOrder = lines.filter((l) => l.toOrder > 0).length;
  const fmt = (n: number) =>
    String(Math.round(n * 100) / 100).replace('.', props.locale === 'en' ? '.' : ',');
  return (
    <Dialog
      title={t('pick.title')}
      wide
      onClose={props.onClose}
      actions={
        <>
          <span className="muted small grow" data-testid="pick-summary">
            {t('pick.summary', { n: lines.length, o: toOrder })}
          </span>
          <button
            disabled={!x || !lines.some((l) => l.fromVan)}
            onClick={() => {
              const next = x!.takeFromVan(van, lines);
              setVan(next);
              writeVan(next);
              props.notify(t('pick.taken'));
            }}
            data-testid="pick-take"
          >
            {t('pick.take')}
          </button>
          <button
            disabled={!x || !toOrder}
            onClick={() =>
              props.save(
                x!.pickListXlsx(project, lines, props.locale),
                `${name} - ${t('pick.file')}.xlsx`,
                'xlsx',
                'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
              )
            }
            data-testid="pick-xlsx"
          >
            {t('pick.excel')}
          </button>
          <button
            disabled={!x || !toOrder}
            onClick={() =>
              props.save(
                new TextEncoder().encode(x!.orderCsv(lines)),
                `${name} - ${t('pick.order')}.csv`,
                'csv',
                'text/csv',
              )
            }
            data-testid="pick-csv"
          >
            {t('pick.csv')}
          </button>
          <button className="primary" onClick={props.onClose}>
            {t('dialog.close')}
          </button>
        </>
      }
    >
      <div className="instr-table">
        <table data-testid="pick-rows">
          <thead>
            <tr>
              <th>{t('pick.ref')}</th>
              <th>{t('pick.desc')}</th>
              <th>{t('pick.needed')}</th>
              <th>{t('pick.inVan')}</th>
              <th>{t('pick.toOrder')}</th>
            </tr>
          </thead>
          <tbody>
            {lines.map((l) => (
              <tr key={l.key} data-key={l.key}>
                <td>{l.reference}</td>
                <td>{l.description}</td>
                <td>
                  {fmt(l.needed)} {l.unit}
                </td>
                <td>
                  <input
                    type="number"
                    min={0}
                    step={l.unit === 'm' ? 1 : 1}
                    value={l.inVan || ''}
                    placeholder="0"
                    aria-label={`${t('pick.inVan')} — ${l.description}`}
                    onChange={(e) => setStock(l.key, Number(e.target.value) || 0)}
                    style={{ width: '6em' }}
                  />
                </td>
                <td className={l.toOrder > 0 ? '' : 'muted'}>{fmt(l.toOrder)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="muted small">{t('pick.hint')}</p>
    </Dialog>
  );
}
